import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Renderer2, } from '@angular/core';
import { FormGroup, FormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CitationStyle, CitationStyleFullName, MendeleyAuthStorage } from 'app/citation/models/citation';
import { CitationService } from 'app/citation/services/citation.service';
import { OAuthHelper } from 'app/citation/utils/oauth-helper';
import { WindowRefService } from 'app/services/window-ref.service';
import { Subscription, timer } from 'rxjs';
import { EditionData, PreparedEdition, } from '../../models/editions';
import { ShareItService } from '../../../share-it/services/share-it.service';
import { ShareItShareBody, ShareItShareEntityType } from '../../../share-it/models/share-it';
import { FeatureToggleService } from '../../../services/feature-toggle.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'citation-export',
  templateUrl: './citation-modal-window.component.html',
  styleUrls: ['./citation-modal-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CitationExportContentComponent extends OAuthHelper implements OnInit, OnDestroy {
  @Input() public editionsData: EditionData[];

  public EMAIL_PATTERN = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,4}$';
  public editionSelect = new UntypedFormControl();
  public editions: PreparedEdition[];
  public MENDELEY_URL: string;
  public isZoteroCollapsed = true;
  public subscription = new Subscription();
  public citationHtml: string;
  public isCitationLoading = false;
  public isCopyMsgVisible = false;
  public isEmailSectionVisible = false;
  public isEmailSuccess = false;
  public isEmailMsgVisible = false;
  public emailMessage: string;
  public citationStyle = CitationStyle;
  public selectedCitationStyle = CitationStyle.MLA;
  public citationStyleFullName = CitationStyleFullName;
  public selectedCitationStyleFullName = CitationStyleFullName.MLA;
  public isCitationMailingEnabled: boolean;
  public form: FormGroup;

  private newWindow: Window;
  private reader: FileReader;
  private readonly REF_WORKS_URL = 'http://www.refworks.com/express/ExpressImport.asp?vendor=Inspire&filter=RIS%20Format&encoding=65001';
  private readonly EASYBIB_URL = 'http://www.easybib.com/impexport/index/add';
  private readonly COINS_CONTAINER = 'coins-container';

  constructor(
    public activeModal: NgbActiveModal,
    public windowRef: WindowRefService,
    private readonly citationService: CitationService,
    private readonly shareItService: ShareItService,
    private readonly renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private readonly cdRef: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly featureToggleService: FeatureToggleService,
    private readonly translateService: TranslateService,
  ) { super(); }

  public ngOnInit() {
    this.isCitationMailingEnabled = this.featureToggleService.getToggles()['VDIS-29183_2025-08-30_citation-sharing'];
    this.citationService.getMendeleyAuthUrl().subscribe((data) => {
      this.MENDELEY_URL = data.url;
      this.cdRef.detectChanges();
    });
    this.prepareEditions();
    this.editionSelect.setValue(this.editions[0].id);

    if (this.isCitationMailingEnabled) {
      this.fetchAndCacheCitation(this.selectedCitationStyle, this.editionSelect.value);
    }

    this.subscription.add(this.editionSelect.valueChanges.subscribe((editionId) => {
      this.fetchAndCacheCitation(this.selectedCitationStyle, editionId);
    }));
    this.cdRef.detectChanges();
  }

  public getPrepareEdition(data: EditionData): PreparedEdition {
    const editionStatement = data.editionStatement && data.editionStatement.length ?
      data.editionStatement[0] : '';
    const publicationDate = data.publicationDate ? data.publicationDate : '';
    let info = editionStatement;
    if (info) {
      info = publicationDate ? `${info}, ${publicationDate}` : info;
    } else if (publicationDate) {
      info = publicationDate;
    } else {
      info = '-';
    }
    return {
      id: data.instanceId,
      editionInfo: info,
    };
  }

  public prepareEditions() {
    if (this.editionsData && this.editionsData.length) {
      const editionsWithoutDate = this.editionsData.
        filter((data: EditionData) => !data.publicationDate);
      const editionsWithDate = this.editionsData.
        filter((data: EditionData) => !!data.publicationDate).
        sort(this.sortEditions);
      this.editionsData = [...editionsWithDate, ...editionsWithoutDate];
      this.editions = this.editionsData.map(this.getPrepareEdition);
    }
  }

  public onRISFile() {
    this.citationService.getRIS(this.editionSelect.value).subscribe((data) => {
      this.downloadFile(data.body, data.fileName);
      this.activeModal.close();
    });
  }

  // TODO: remove Mendeley functionalities when implementing work for: VDIS-34806
  public onMendeley() {
    if (this.shouldAuthorize(MendeleyAuthStorage)) {
      this.authorizeInMendeley(this.MENDELEY_URL);
    } else if (this.shouldRefreshToken(MendeleyAuthStorage)) {
      this.refreshMendeleyAuthToken();
    } else {
      this.exportToMendeleyTool();
    }
  }

  public authorizeInMendeley(url: string) {
    this.windowRef.nativeWindow().open(url, '_blank');
    this.windowRef.nativeWindow().addEventListener('storage', this.mendeleyAuthCodeReceiver.bind(this));
  }

  public refreshMendeleyAuthToken() {
    const refreshToken = this.getRefreshToken(MendeleyAuthStorage);
    this.citationService.refreshMendeleyAuthToken(refreshToken).subscribe((data) => {
      this.updateAuthStorage(MendeleyAuthStorage, data);
      this.exportToMendeleyTool();
    });
  }

  public mendeleyAuthCodeReceiver(event: StorageEvent) {
    if (event.newValue && event.key === MendeleyAuthStorage.CODE) {
      this.citationService.getMendeleyAuthToken(event.newValue).subscribe((data) => {
        this.updateAuthStorage(MendeleyAuthStorage, data);
        this.exportToMendeleyTool();
      });
    }
  }

  public exportToMendeleyTool() {
    this.citationService.getMendeleyData(this.editionSelect.value).subscribe((data) => {
      const token = this.windowRef.nativeWindow().localStorage.getItem(MendeleyAuthStorage.ACCESS_TOKEN);
      this.citationService.sendDataToMendeley(data, token).subscribe();
      this.activeModal.close();
    });
  }

  public onZotero() {
    if (this.isZoteroCollapsed) {
      this.addCoinsData(this.editionSelect.value);
    }
    this.isZoteroCollapsed = !this.isZoteroCollapsed;
    this.cdRef.detectChanges();
  }

  public onZoteroRISFile() {
    this.citationService.getRIS(this.editionSelect.value).subscribe((data) => {
      this.downloadFile(data.body, data.fileName);
      this.activeModal.close();
    });
  }

  public onZoteroPlugin() {
    this.activeModal.close();
  }

  public downloadFile(body: Blob, fileName: string): void {
    fileName = fileName ? fileName : 'Inspire_RIS_Export.ris';
    if (this.windowRef.nativeWindow().navigator && this.windowRef.nativeWindow().navigator.msSaveOrOpenBlob) {
      this.windowRef.nativeWindow().navigator.msSaveOrOpenBlob(body, fileName);
    } else {
      const url = this.windowRef.nativeWindow().URL.createObjectURL(body);
      const link = this.renderer.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      this.windowRef.nativeWindow().URL.revokeObjectURL(url);
      link.remove();
    }
  }

  public onRefworks() {
    this.newWindow = this.windowRef.nativeWindow().open('', '_blank');
    this.citationService.getDataForRefWorks(this.editionSelect.value).subscribe((data: Blob) => {
      this.readFileFromBlob(data, () => {
        this.openRefWorksTool(this.reader.result as string);
        this.activeModal.close();
      });
    });
  }

  public openRefWorksTool(data: string) {
    const refWorksRequestTemplate =
      `<html>
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <link rel="stylesheet" type="text/css" href="style.css" />
        </head>
        <body onload="document.getElementById(\'exportForm\').submit();">
          <FORM accept-charset="UTF-8"
            name="ExportForm"
            id="exportForm"
            method="POST"
            target="_parent"
            style="display:none"
            action=${this.REF_WORKS_URL}>
          ${data}
        </body>
      </html>`;
    this.newWindow.document.open();
    this.newWindow.document.write(refWorksRequestTemplate);
    this.newWindow.document.close();
  }

  public onEasybib() {
    this.newWindow = this.windowRef.nativeWindow().open('', '_blank');
    this.citationService.getDataForEasyBib(this.editionSelect.value).subscribe((data: Blob) => {
      this.readFileFromBlob(data, () => {
        this.openEasyBibTool(this.reader.result as string);
        this.activeModal.close();
      });
    });
  }

  public openEasyBibTool(data: string) {
    const easyBibRequestTemplate =
      `<html>
        <head>
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        </head>
        <body onload="document.getElementById(\'exportForm\').submit();">
          <form accept-charset="UTF-8"
              name="ExportForm"
              id="exportForm"
              method="POST"
              target="_parent"
              style="display:none"
              action="${this.EASYBIB_URL}">
            ${data}
            <input type="submit">
          </form>
        </body>
      </html>`;
    this.newWindow.document.open();
    this.newWindow.document.write(easyBibRequestTemplate);
    this.newWindow.document.close();
  }

  public onClose() {
    this.activeModal.dismiss('Cross click');
  }

  public trackById(index: number, item: PreparedEdition) {
    return item.id;
  }

  public onCitationStyleChange(citationStyle: CitationStyle, citationStyleFullName: CitationStyleFullName): void {
    this.selectedCitationStyle = citationStyle;
    this.selectedCitationStyleFullName = citationStyleFullName;
    this.fetchAndCacheCitation(citationStyle, this.editionSelect.value);
  }

  public onCopyCitation(): void {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = this.citationHtml;

    const type = 'text/html';
    const blob = new Blob([tempElement.innerHTML], { type });
    const data = [new ClipboardItem({ [type]: blob })];

    navigator.clipboard.write(data).then(() => {
      this.isCopyMsgVisible = true;
      this.cdRef.detectChanges();

      timer(3000).subscribe(() => {
        this.isCopyMsgVisible = false;
        this.cdRef.detectChanges();
      });
    });
  }

  public toggleEmailSection(): void {
    if (!this.form) {
      this.form = this.fb.group({
        email: ['', [Validators.required, Validators.email, Validators.pattern(this.EMAIL_PATTERN)]],
      });
    }
    this.isEmailSectionVisible = !this.isEmailSectionVisible;
  }

  get emailControl() {
    return this.form.get('email');
  }

  get citationHtmlSanitized(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.citationHtml);
  }

  public sendEmail(): void {
    if (this.form && this.form.valid) {
      const payload = this.buildEmailPayload();

      this.subscription.add(this.shareItService.shareItShare(payload).subscribe({
        next: () => this.showEmailMessage(true, this.translateService.instant('exportCitationEmailSuccessMsg')),
        error: () => this.showEmailMessage(false, this.translateService.instant('exportCitationEmailFailedMsg')),
      }));
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public fetchAndCacheCitation(style: CitationStyle, editionId: string) {
    this.isCitationLoading = true;
    this.subscription.add(
      this.citationService.fetchCitation(style, editionId).subscribe({
        next: (citationHtml) => {
          this.citationHtml = citationHtml;
          const tempElement = document.createElement('div');
          tempElement.innerHTML = citationHtml;
          this.isCitationLoading = false;
          this.cdRef.detectChanges();
        },
        error: () => {
          this.isCitationLoading = false;
          this.cdRef.detectChanges();
        }
      })
    );
  }

  public buildEmailPayload(): ShareItShareBody {
    const email = this.form.get('email')?.value;
    const selectedInstanceId = this.editionSelect.value;
    const selectedEdition = this.editionsData.find(e => e.instanceId === selectedInstanceId);
    const recordId = selectedEdition.recordId;

    return {
      to: email,
      subject: 'Vega Discover Citation',
      resource: {
        type: ShareItShareEntityType.CITATION,
        attributes: {
          url: this.windowRef.origin() + `/search/card?recordId=${recordId}`,
          title: 'Vega Discover Title',
          citation: this.citationHtml,
          citationStyle: this.selectedCitationStyleFullName,
        }
      },
    };
  }

  public showEmailMessage(success: boolean, message: string): void {
    this.isEmailSuccess = success;
    this.emailMessage = message;
    this.isEmailMsgVisible = true;
    this.cdRef.detectChanges();

    timer(3000).subscribe(() => {
      this.isEmailMsgVisible = false;
      this.cdRef.detectChanges();
    });
  }

  private addCoinsData(instanceId: string) {
    if (!this.isPresent(instanceId)) {
      this.citationService.getCoinsData(instanceId).subscribe((data) => {
        const span = this.renderer.createElement('span');
        span.setAttribute('entry-id', instanceId);
        span.setAttribute('class', 'Z3988');
        span.setAttribute('title', data);
        span.setAttribute('entry-id', instanceId);
        this.getCoinsContainer().appendChild(span);
        this.windowRef.nativeWindow().document.dispatchEvent(new Event('ZoteroItemUpdated', {
          bubbles: true,
          cancelable: true,
        }));
      });
    }
  }

  private getCoinsContainer(): HTMLElement {
    let coinsContainer = this.windowRef.nativeWindow().document.getElementById(this.COINS_CONTAINER);
    if (coinsContainer == null) {
      const body = this.windowRef.nativeWindow().document.getElementsByTagName('body')[0];
      coinsContainer = this.renderer.createElement('div');
      coinsContainer.setAttribute('id', this.COINS_CONTAINER);
      body.appendChild(coinsContainer);
    }
    return coinsContainer;
  }

  private isPresent(instanceId: string) {
    const spans = this.getCoinsContainer().getElementsByTagName('span');
    return Array.from(spans).some((span) => span.getAttribute('entry-id') === instanceId);
  }

  private sortEditions(a: EditionData, b: EditionData) {
    const aDate = new Date(a.publicationDate);
    const bDate = new Date(b.publicationDate);
    return (aDate > bDate) ? -1 : (aDate < bDate) ? 1 : 0;
  }

  private readFileFromBlob(data: Blob, handler: (e: Event) => void) {
    this.reader = new FileReader();
    this.reader.addEventListener('loadend', handler);
    this.reader.readAsText(data);
  }
}
